import { useEffect, useRef, useState } from "react";
import { register, call, remove } from "../funcStore";
import { searchVtubers } from "../api/searchApi";
import { logAction } from "../api/logApi";
import Header from "../components/layout/Header";
import MainTabs from "../components/layout/MainTabs";
import Footer from "../components/layout/Footer";
import VTuberCard from "../components/vtuber/VTuberCard";
import ActiveFilterChips from "../components/filters/ActiveFilterChips";
import FilterSelector from "../components/filters/FilterSelector";
import {
  FILTER_GROUPS,
  FILTER_REV,
  filter_reverse,
} from "../data/filterGroups";
import { recordSearchFilters, getTopAttributes } from "../utils/userPreference";

const SORT_OPTIONS = [
  { value: "new", label: "新着順" },
  { value: "recommended", label: "おすすめ順" },
  { value: "subscriber_desc", label: "登録者数が多い順" },
];

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  searchMain: {
    flex: 1,
    display: "flex",
    gap: "24px",
    padding: "24px",
    alignItems: "flex-start",
  },
  spSearchMain: {
    gap: "24px",
    padding: "24px",
    alignItems: "flex-start",
  },
  resultList: {
    flex: 1,
  },
  resultGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "16px",
  },
  resultBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    margin: "0 0 12px",
  },
  resultSummary: {
    margin: 0,
    fontSize: "0.85rem",
    color: "#555",
  },
  sortSelect: {
    fontSize: "0.85rem",
    padding: "4px 8px",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginTop: "20px",
  },
  pageButton: {
    padding: "6px 14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    background: "#fff",
    cursor: "pointer",
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: "default",
  },
};

const PAGE_SIZE = 50;

function SearchPage() {
  const COMPONENT_KEY = "searchPage";

  //state
  const STATE_BEFORE_INIT = "beforeInit";
  const STATE_IDLE = "idle";
  const STATE_PAGE_UP_CAHNGE = "pageUpChange";
  const STATE_PAGE_DOWN_CAHNGE = "pageDownChange";
  const STATE_SEARCHING = "searching";
  const STATE_RESULTS_SHOWN = "resultsShown";
  const STATE_CLICK_SEARCH = "clickSearch";
  const STATE_NO_RESULTS = "noResults";
  const STATE_CLEAR_FILTER = "clearFilter";
  const STATE_SORT_CHANGE = "sortChange";

  const [state, setState] = useState(STATE_BEFORE_INIT);

  //data
  const dataRef = useRef({
    filters: {},
    results: [],
    lastLoggedFilters: null,
    total: 0,
    page: 1,
    sort: "new",
    mobile: call("PCSP.getState"),
  });

  // effect
  useEffect(() => {
    if (state === STATE_BEFORE_INIT) {
      dataRef.current.page = 1;
      init();
      getSessionTag();
    }
    if (state === STATE_IDLE) {
      dataRef.current.page = 1;
      const hasDetailTag = getDetailTag();
      setState(STATE_SEARCHING);
    }
    if (state === STATE_PAGE_UP_CAHNGE) {
      dataRef.current.page += 1;
      setState(STATE_SEARCHING);
    }
    if (state === STATE_PAGE_DOWN_CAHNGE) {
      dataRef.current.page -= 1;
      setState(STATE_SEARCHING);
    }
    if (state === STATE_CLICK_SEARCH) {
      dataRef.current.page = 1;
      const name = call("NameInputGatValue");
      dataRef.current.filters = {
        ...dataRef.current.filters,
        vtuberName: { vtuberName: name },
      };
      setState(STATE_SEARCHING);
    }
    if (state === STATE_SEARCHING) {
      (async () => {
        await search();
        setState(
          dataRef.current.results.length > 0
            ? STATE_RESULTS_SHOWN
            : STATE_NO_RESULTS,
        );
        call("activeFilterChips.reflesh");
        call("filterSelector.setSearching", false);
      })();
    }
    if (state === STATE_CLEAR_FILTER) {
      dataRef.current.page = 1;
      const targetTag = call("activeFilterChips.getClearTarget");
      if (targetTag === "name") {
        call("NameInputClearName");
      }
      const { key, groupKey } = filter_reverse(targetTag);
      call("searchPage.setFilter", groupKey, key, "");
      call("activeFilterChips.reflesh");
      setState(STATE_SEARCHING);
    }
    if (state === STATE_SORT_CHANGE) {
      dataRef.current.page = 1;
      setState(STATE_SEARCHING);
    }
  }, [state]);

  useEffect(() => {
    return () => {
      remove("searchPage.setFilter");
      remove("searchPage.submit");
      remove("searchPage.clickSearch");
      remove("searchPage.getFilterGroup");
    };
  }, []);

  // effect function
  // from STATE_BEFORE_INIT
  const init = () => {
    register("searchPage.getFilter", (groupKey) => {
      return dataRef.current.filters[groupKey];
    });

    register("searchPage.getAllFilter", () => dataRef.current.filters);

    register("searchPage.setFilter", (groupKey, key, value) => {
      dataRef.current.filters[groupKey][key] = value ?? "";
      call(`filterGroup.${groupKey}.update`);
      call(`filterGroup.${groupKey}.open`);
    });

    register("searchPage.setFilterName", (value) => {
      dataRef.current.filters["vtuberName"]["vtuberName"] = value ?? "";
    });

    register("searchPage.setFilterByTag", (tag) => {
      const { groupKey, key } = FILTER_REV[tag];
      call("searchPage.setFilter", groupKey, key, tag);
      setState(STATE_IDLE);
    });

    register("searchPage.submit", async () => {
      setState(STATE_IDLE);
    });

    register("searchPage.clickSearch", () => {
      setState(STATE_CLICK_SEARCH);
    });

    register("searchPage.getVtuberById", (id) => {
      return dataRef.current.results.filter((v) => v.id == id)[0];
    });

    register("searchPage.getFilterGroup", (groupKey) =>
      FILTER_GROUPS.find((group) => group.key === groupKey),
    );

    register(`${COMPONENT_KEY}.getTotalPage`, () => {
      return Math.max(1, Math.ceil(dataRef.current.total / PAGE_SIZE));
    });

    register(`${COMPONENT_KEY}.clearFilter`, () => {
      setState(STATE_CLEAR_FILTER);
    });

    initFilters();
    setState(STATE_IDLE);
  };

  function initFilters() {
    const allFilters = {
      vtuberName: { vtuberName: "" },
    };
    FILTER_GROUPS.forEach((filter) => {
      const groupFilters = {};
      filter.filters.forEach((f) => {
        groupFilters[f.key] = "";
      });
      allFilters[filter.key] = groupFilters;
    });
    dataRef.current.filters = allFilters;
  }

  // from STATE_IDLE
  function getDetailTag() {
    const tag = call("vtuberProfile.getClickTag");
    if (tag) {
      call("searchPage.setFilterByTag", tag);
    }
    return !!tag
  }

  // from STATE_IDLE
  function getSessionTag() {
    const sessionTags = JSON.parse(sessionStorage.getItem("filter"));
    if(sessionTags){
      dataRef.current.filters = sessionTags
    }
  }

  // from STATE_SEARCHING
  async function search() {
    const appliedFilters = Object.assign(
      {},
      ...Object.values(dataRef.current.filters),
    );

    sessionStorage.setItem("filter",JSON.stringify(dataRef.current.filters))

    // FilterGroupの初期マウント時にも呼ばれるため、条件に変化がない再送信はログしない
    const filtersKey = JSON.stringify(appliedFilters);
    if (filtersKey !== dataRef.current.lastLoggedFilters) {
      dataRef.current.lastLoggedFilters = filtersKey;
      logAction("search_submit", { filters: appliedFilters });
      recordSearchFilters(appliedFilters);
    }

    const prefTags =
      dataRef.current.sort === "recommended"
        ? getTopAttributes(5).map(({ value }) => value)
        : [];

    const [results, total] = await searchVtubers(
      appliedFilters,
      dataRef.current.page,
      dataRef.current.sort,
      prefTags,
    );
    dataRef.current.results = results;
    dataRef.current.total = total;
  }

  // regist Func

  const handleSortChange = (e) => {
    logAction("sort_change", { sort: e.target.value });
    dataRef.current.sort = e.target.value;
    setState(STATE_SORT_CHANGE);
  };

  const handlePageChange = (direction) => {
    logAction("pagination", { direction, page: dataRef.current.page });
    setState(
      direction === "next" ? STATE_PAGE_UP_CAHNGE : STATE_PAGE_DOWN_CAHNGE,
    );
  };

  // jsxbilder
  if (state === STATE_BEFORE_INIT) {
    return null;
  }
  return (
    <div style={styles.page}>
      <Header />

      <MainTabs />

      <main
        style={
          dataRef.current.mobile === "pc"
            ? styles.searchMain
            : styles.spSearchMain
        }
      >
        <FilterSelector />

        <section style={styles.resultList}>
          <ActiveFilterChips />

          {state === STATE_SEARCHING && <p>検索中...</p>}

          {state === STATE_NO_RESULTS && (
            <p>条件に一致するVTuberが見つかりませんでした。</p>
          )}

          {state === STATE_RESULTS_SHOWN && (
            <>
              <div style={styles.resultBar}>
                <p style={styles.resultSummary}>
                  検索結果 {dataRef.current.total}件
                </p>

                <select
                  style={styles.sortSelect}
                  defaultValue={dataRef.current.sort}
                  onChange={handleSortChange}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.resultGrid}>
                {dataRef.current.results.map((vtuber) => (
                  <VTuberCard key={vtuber.id} vtuberId={vtuber.id} />
                ))}
              </div>

              {call(`${COMPONENT_KEY}.getTotalPage`) > 1 && (
                <div style={styles.pagination}>
                  <button
                    type="button"
                    style={{
                      ...styles.pageButton,
                      ...(dataRef.current.page === 1
                        ? styles.pageButtonDisabled
                        : {}),
                    }}
                    onClick={() => handlePageChange("prev")}
                    disabled={dataRef.current.page === 1}
                  >
                    前へ
                  </button>

                  <span>
                    {dataRef.current.page} /{" "}
                    {call(`${COMPONENT_KEY}.getTotalPage`)}
                  </span>

                  <button
                    type="button"
                    style={{
                      ...styles.pageButton,
                      ...(dataRef.current.page ===
                      call(`${COMPONENT_KEY}.getTotalPage`)
                        ? styles.pageButtonDisabled
                        : {}),
                    }}
                    onClick={() => handlePageChange("next")}
                    disabled={
                      dataRef.current.page ===
                      call(`${COMPONENT_KEY}.getTotalPage`)
                    }
                  >
                    次へ
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SearchPage;
